import {
    compareActivityJobsNewestFirst,
    hasConflictingActivityBuildingIds,
    representativeActivityJob,
} from './job';
import type { ActivityItem, ActivityJob, ActivitySession } from './models';

export const MAX_STORED_ITEMS = 250;
export const MAX_STORED_JOBS_PER_ITEM = 250;
export const MAX_TOMBSTONES = 500;
export const MAX_ACTIVITY_MESSAGE_LENGTH = 1_000;

export function isActivityIntegrityQuarantined(item: ActivityItem): boolean {
    return (
        item.identityQuarantined === true ||
        (item.availability === 'unavailable' &&
            (item.jobs.length >= MAX_STORED_JOBS_PER_ITEM ||
                hasConflictingActivityBuildingIds(item.jobs)))
    );
}

export function boundActivitySession(
    session: ActivitySession
): ActivitySession {
    const items = [...session.items]
        .sort(
            (left, right) =>
                Date.parse(right.updatedAtUtc) - Date.parse(left.updatedAtUtc)
        )
        .slice(0, MAX_STORED_ITEMS)
        .map((item) => {
            const integrityQuarantined = isActivityIntegrityQuarantined(item);
            const jobs = boundActivityJobs(item.jobs);
            const overflowed = jobs.length < item.jobs.length;

            return {
                ...item,
                jobs,
                observedJobIds: boundObservedJobIds(item, jobs),
                identityQuarantined:
                    item.identityQuarantined === true ||
                    hasConflictingActivityBuildingIds(jobs),
                availability:
                    overflowed || integrityQuarantined
                        ? ('unavailable' as const)
                        : item.availability,
                seen: overflowed || integrityQuarantined ? false : item.seen,
            };
        });
    const dismissalTombstones =
        session.dismissalTombstones.slice(-MAX_TOMBSTONES);
    const retainedTombstones = new Set(dismissalTombstones);

    return {
        items,
        dismissalTombstones,
        dismissalTombstoneMetadata: session.dismissalTombstoneMetadata
            .filter(
                (metadata) =>
                    retainedTombstones.has(metadata.key) &&
                    hasValidObservedJobIds(metadata.observedJobIds) &&
                    Number.isFinite(Date.parse(metadata.latestObservedAtUtc))
            )
            .slice(-MAX_TOMBSTONES),
    };
}

function boundObservedJobIds(
    item: ActivityItem,
    retainedJobs: readonly ActivityJob[]
): readonly string[] {
    const observedJobIds = item.observedJobIds ?? [
        ...new Set(item.jobs.map((job) => job.jobId)),
    ];

    return observedJobIds.length <= MAX_STORED_JOBS_PER_ITEM
        ? observedJobIds
        : retainedJobs.map((job) => job.jobId);
}

function boundActivityJobs(
    jobs: readonly ActivityJob[]
): readonly ActivityJob[] {
    if (jobs.length === 0) return [];

    const representative = representativeActivityJob(jobs);
    const newestJobs = [...jobs]
        .sort(compareActivityJobsNewestFirst)
        .slice(0, MAX_STORED_JOBS_PER_ITEM);
    const retainedJobs = newestJobs.some(
        (job) => job.jobId === representative.jobId
    )
        ? newestJobs
        : [...newestJobs.slice(0, -1), representative];

    return retainedJobs.map((job, index) =>
        index === 0 || job.jobId === representative.jobId
            ? job
            : { ...job, message: null }
    );
}

function hasValidObservedJobIds(jobIds: readonly string[]): boolean {
    return (
        jobIds.length > 0 &&
        jobIds.length <= MAX_STORED_JOBS_PER_ITEM &&
        new Set(jobIds).size === jobIds.length &&
        jobIds.every((jobId) => jobId.length > 0 && jobId === jobId.trim())
    );
}
